import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Save, Plus, Trash2, Search, RefreshCw, Tag } from 'lucide-react';
import { api, discountsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const OrderEditModal = ({ orderId, onClose, onSave }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // Cashiers may only change payment method — no order items UI
  const canEditItems = user?.role !== 'cashier';
  const [items, setItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [replacingIndex, setReplacingIndex] = useState(null);
  const [selectedDiscounts, setSelectedDiscounts] = useState([]);
  const [showDiscountPanel, setShowDiscountPanel] = useState(false);

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => api.get(`/orders/${orderId}`).then(r => r.data)
  });

  const { data: orderItems } = useQuery({
    queryKey: ['order-items', orderId],
    queryFn: () => api.get(`/orders/${orderId}/items`).then(r => r.data),
    enabled: !!orderId && canEditItems
  });

  const { data: productsData } = useQuery({
    queryKey: ['products-search', productSearch],
    queryFn: () => api.get(`/products?search=${productSearch}`).then(r => r.data),
    enabled: canEditItems && showSearch && productSearch.length > 0
  });

  const { data: discountsData } = useQuery({
    queryKey: ['discounts-active'],
    queryFn: () => discountsAPI.getAll({ params: { is_active: true, limit: 50 } }).then(r => r.data),
    enabled: canEditItems
  });

  useEffect(() => {
    if (order) setPaymentMethod(order.payment_method || '');
  }, [order]);

  useEffect(() => {
    if (orderItems) {
      setItems(orderItems.map(i => ({
        id: i.id,
        product_id: i.product_id,
        product_name: i.name,
        product_type: i.product_type || 'general',
        sku: i.sku,
        quantity: i.quantity,
        unit_price: parseFloat(i.unit_price),
        total_price: parseFloat(i.total_price)
      })));
    }
  }, [orderItems]);

  const updateMutation = useMutation({
    mutationFn: (data) => api.put(`/orders/${orderId}`, data),
    onSuccess: () => {
      toast.success('Order updated successfully!');
      queryClient.invalidateQueries(['order', orderId]);
      queryClient.invalidateQueries(['orders']);
      queryClient.invalidateQueries(['order-items', orderId]);
      onSave?.();
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to update order')
  });

  const products = productsData?.products || [];
  const availableDiscounts = discountsData?.discounts || [];

  const subtotal = items.reduce((s, i) => s + i.total_price, 0);

  const discountAmount = useMemo(() => {
    let total = 0;
    selectedDiscounts.forEach(discount => {
      let discountProductTypes = [];
      if (discount.product_types) {
        try {
          discountProductTypes = typeof discount.product_types === 'string'
            ? JSON.parse(discount.product_types)
            : discount.product_types;
        } catch (e) { discountProductTypes = []; }
      }

      const matchingItems = items.filter(item => {
        if (discountProductTypes.length === 0) return true;
        return discountProductTypes.includes(item.product_type || 'general');
      });

      const matchingSubtotal = matchingItems.reduce((s, i) => s + i.total_price, 0);
      if (matchingItems.length === 0) return;

      switch (discount.type) {
        case 'percentage':
          total += matchingSubtotal * (discount.value / 100);
          break;
        case 'fixed_amount':
          total += Math.min(discount.value, matchingSubtotal);
          break;
        case 'bottle_return':
          if (discount.bottle_return_count) {
            const tiers = [
              { bottles: 1, amount: 1000 }, { bottles: 2, amount: 2000 },
              { bottles: 3, amount: 3000 }, { bottles: 4, amount: 4000 }
            ];
            const tier = tiers.find(t => t.bottles === discount.bottle_return_count);
            if (tier) total += Math.min(tier.amount, matchingSubtotal);
          }
          break;
      }
    });
    return total;
  }, [selectedDiscounts, items]);

  const total = Math.max(0, subtotal - discountAmount);

  const handleToggleDiscount = (discount) => {
    const isSelected = selectedDiscounts.find(d => d.id === discount.id);
    if (isSelected) {
      setSelectedDiscounts(prev => prev.filter(d => d.id !== discount.id));
    } else {
      if (discount.type === 'bottle_return') {
        const existing = selectedDiscounts.find(d => d.type === 'bottle_return');
        if (existing) {
          toast.error('Only one bottle return discount can be applied at a time');
          return;
        }
      }
      setSelectedDiscounts(prev => [...prev, discount]);
    }
  };

  const handleSelectProduct = (product) => {
    if (replacingIndex !== null) {
      const updated = [...items];
      const qty = updated[replacingIndex].quantity;
      updated[replacingIndex] = {
        id: null,
        product_id: product.id,
        product_name: product.name,
        product_type: product.product_type || 'general',
        sku: product.sku,
        quantity: qty,
        unit_price: parseFloat(product.price),
        total_price: qty * parseFloat(product.price)
      };
      setItems(updated);
      toast.success(`Replaced with ${product.name}`);
      setReplacingIndex(null);
    } else {
      const existing = items.findIndex(i => i.product_id === product.id);
      if (existing >= 0) {
        const updated = [...items];
        updated[existing].quantity += 1;
        updated[existing].total_price = updated[existing].quantity * updated[existing].unit_price;
        setItems(updated);
      } else {
        setItems(prev => [...prev, {
          id: null,
          product_id: product.id,
          product_name: product.name,
          product_type: product.product_type || 'general',
          sku: product.sku,
          quantity: 1,
          unit_price: parseFloat(product.price),
          total_price: parseFloat(product.price)
        }]);
      }
      toast.success(`${product.name} added`);
    }
    setShowSearch(false);
    setProductSearch('');
  };

  const handleQty = (index, qty) => {
    if (qty < 1) return;
    const updated = [...items];
    updated[index].quantity = qty;
    updated[index].total_price = qty * updated[index].unit_price;
    setItems(updated);
  };

  const handleRemove = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!paymentMethod) return toast.error('Select a payment method');

    if (!canEditItems) {
      updateMutation.mutate({ payment_method: paymentMethod });
      return;
    }

    if (items.length === 0) return toast.error('Add at least one item');
    updateMutation.mutate({
      items: items.map(i => ({
        product_id: i.product_id,
        quantity: i.quantity,
        unit_price: i.unit_price
      })),
      discount_amount: discountAmount,
      total_amount: paymentMethod === 'gift' ? 0 : total,
      payment_method: paymentMethod,
      discounts: selectedDiscounts.map(d => ({
        discount_id: d.id,
        discount_type: d.type,
        discount_value: d.value
      }))
    });
  };

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'RWF' }).format(n || 0);

  const getDiscountLabel = (discount) => {
    if (discount.type === 'percentage') return `${discount.value}%`;
    if (discount.type === 'fixed_amount') return fmt(discount.value);
    if (discount.type === 'bottle_return') return `${discount.bottle_return_count} bottles`;
    return '';
  };

  if (isLoading) return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Loading order...</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg w-full mx-4 max-h-[90vh] overflow-y-auto ${canEditItems ? 'max-w-2xl' : 'max-w-md'}`}>
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {canEditItems ? 'Edit Order' : 'Edit Payment Method'}
            </h2>
            <p className="text-sm text-gray-500">#{order?.order_number}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            <select
              value={paymentMethod}
              onChange={e => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select payment method</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="mobile_money">Mobile Money</option>
              <option value="momo">MoMo</option>
              <option value="split">Split</option>
              {user?.role === 'admin' && <option value="gift">Gift (Free)</option>}
            </select>
          </div>

          {/* Order Items / discounts / totals — admin & other roles only */}
          {canEditItems && (
            <>
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Order Items</h3>
                <button
                  onClick={() => { setReplacingIndex(null); setShowSearch(true); setProductSearch(''); }}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add Item
                </button>
              </div>

              {showSearch && (
                <div className="p-3 border rounded-lg bg-gray-50">
                  {replacingIndex !== null && (
                    <p className="text-xs text-blue-600 mb-2">Replacing: <strong>{items[replacingIndex]?.product_name}</strong></p>
                  )}
                  <div className="flex gap-2 mb-2">
                    <Search className="w-4 h-4 text-gray-400 mt-2.5" />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={e => setProductSearch(e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => { setShowSearch(false); setProductSearch(''); setReplacingIndex(null); }}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
                    >Cancel</button>
                  </div>
                  {products.length > 0 && (
                    <div className="max-h-48 overflow-y-auto divide-y">
                      {products.map(p => (
                        <div key={p.id} onClick={() => handleSelectProduct(p)}
                          className="flex justify-between items-center p-2 hover:bg-white cursor-pointer rounded">
                          <div>
                            <p className="text-sm font-medium">{p.name}</p>
                            <p className="text-xs text-gray-500">{p.sku}</p>
                          </div>
                          <p className="text-sm font-medium">{fmt(p.price)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                {items.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.product_name}</p>
                      <p className="text-xs text-gray-500">{item.sku}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleQty(i, item.quantity - 1)}
                        className="w-7 h-7 flex items-center justify-center border rounded hover:bg-gray-50 text-sm">−</button>
                      <span className="w-8 text-center text-sm">{item.quantity}</span>
                      <button onClick={() => handleQty(i, item.quantity + 1)}
                        className="w-7 h-7 flex items-center justify-center border rounded hover:bg-gray-50 text-sm">+</button>
                    </div>
                    <div className="text-right min-w-[80px]">
                      <p className="text-sm font-medium">{fmt(item.total_price)}</p>
                      <p className="text-xs text-gray-500">{fmt(item.unit_price)} each</p>
                    </div>
                    <button onClick={() => { setReplacingIndex(i); setShowSearch(true); setProductSearch(''); }}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Replace item">
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleRemove(i)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {items.length === 0 && (
                  <p className="text-center py-8 text-gray-400 text-sm">
                    No items. Click "Add Item" to add products.
                  </p>
                )}
              </div>

              {items.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowDiscountPanel(p => !p)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700"
                  >
                    <span className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-green-600" />
                      Discounts
                      {selectedDiscounts.length > 0 && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                          {selectedDiscounts.length} applied — -{fmt(discountAmount)}
                        </span>
                      )}
                    </span>
                    <span className="text-gray-400">{showDiscountPanel ? '▲' : '▼'}</span>
                  </button>

                  {showDiscountPanel && (
                    <div className="p-3 space-y-2 max-h-56 overflow-y-auto">
                      {availableDiscounts.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">No active discounts available</p>
                      ) : (
                        availableDiscounts.map(discount => {
                          const isSelected = !!selectedDiscounts.find(d => d.id === discount.id);
                          return (
                            <div
                              key={discount.id}
                              onClick={() => handleToggleDiscount(discount)}
                              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  isSelected ? 'border-green-500 bg-green-500' : 'border-gray-300'
                                }`}>
                                  {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{discount.name}</p>
                                  {discount.description && (
                                    <p className="text-xs text-gray-500">{discount.description}</p>
                                  )}
                                </div>
                              </div>
                              <span className="text-sm font-semibold text-green-600">{getDiscountLabel(discount)}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}

              {items.length > 0 && (
                <div className="space-y-1.5 pt-3 border-t text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>{fmt(subtotal)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({selectedDiscounts.map(d => d.name).join(', ')})</span>
                      <span>-{fmt(discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-base pt-1 border-t">
                    <span>Total</span>
                    <span>{paymentMethod === 'gift' ? fmt(0) : fmt(total)}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 pb-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={updateMutation.isPending}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            <Save className="w-4 h-4" />
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderEditModal;
